/**
 * Refresh Sheet (Options) Action
 * Scans active sheet used range for option entities, re-fetches snapshots, writes back in place
 */

/* global Excel */

import { runExcel } from "../../excel/runExcel";
import { logger } from "../../shared/logger";
import { buildOptionEntityEnrichedFromOccSymbol } from "../../services/options/optionEntityFactory";
import { scanActiveSheetForOptionEntities } from "../../shared/excel/scanForOptionEntities";
import { sleep, jitter } from "../../shared/async/limitConcurrency";
import { diagnosticsStore, safeErrorMessage, categorizeError } from "../../shared/diagnostics/diagnosticsStore";
import type { DiagnosticErrorCategory } from "../../shared/diagnostics/types";
import type { ProgressCallback, RefreshResult } from "./refreshSelectedOptions";
import {
  MAX_ITEMS_PER_RUN,
  CONFIRM_THRESHOLD,
  REQUESTS_PER_SECOND,
  JITTER_MS_MAX,
} from "./refreshConstants";
import { showConfirm } from "../ui/confirm";

export type RefreshSheetOptionsResult = RefreshResult & {
  noEntitiesFound?: boolean;
  total?: number;
  refreshed?: number;
  failed?: number;
  skipped?: number;
};

/**
 * Refresh all option entities on the active sheet
 */
export async function refreshSheetOptionsAction(
  onProgress?: ProgressCallback
): Promise<RefreshSheetOptionsResult> {
  const opStartTime = Date.now();
  const opCallId = diagnosticsStore.recordCallStart(
    "OPTIONS.REFRESH_SHEET",
    "/refresh-sheet-options"
  );

  try {
    const scan = await scanActiveSheetForOptionEntities();

    if (scan.items.length === 0) {
      diagnosticsStore.recordCallEnd(opCallId, { ok: true, ms: Date.now() - opStartTime });
      return { errors: [], noEntitiesFound: true };
    }

    if (scan.items.length > MAX_ITEMS_PER_RUN) {
      const msg =
        "Found " +
        scan.items.length +
        " option entities, but the max per run is " +
        MAX_ITEMS_PER_RUN +
        ". Narrow your used range or run on a smaller sheet.";
      diagnosticsStore.recordCallEnd(opCallId, { ok: false, ms: Date.now() - opStartTime });
      return {
        errors: [{ address: "", message: msg }],
      };
    }

    if (scan.items.length >= CONFIRM_THRESHOLD) {
      const rangePart =
        scan.usedRangeAddress && scan.usedRangeAddress.length > 0
          ? " Used range: " + scan.usedRangeAddress + "."
          : "";
      const countPart =
        scan.scannedCellCount !== undefined
          ? " " + scan.scannedCellCount + " cells scanned."
          : "";
      const confirmed = await showConfirm({
        title: "Refresh " + scan.items.length + " option entities?",
        message:
          "This operation is rate-limited (~" +
          REQUESTS_PER_SECOND +
          "/s) and may take a moment." +
          rangePart +
          countPart,
        confirmLabel: "Refresh",
        cancelLabel: "Cancel",
      });
      if (!confirmed) {
        diagnosticsStore.recordCallEnd(opCallId, { ok: true, ms: Date.now() - opStartTime });
        return { errors: [] };
      }
    }

    const total = scan.items.length;
    let done = 0;
    let refreshed = 0;
    let failed = 0;
    const skipped = 0;

    const writebacks: Array<{ address: string; entityCellValue: any }> = [];
    const errors: Array<{ address: string; message: string }> = [];

    const baseDelayMs = Math.ceil(1000 / REQUESTS_PER_SECOND);

    for (let i = 0; i < scan.items.length; i++) {
      if (i > 0) {
        await sleep(baseDelayMs + jitter(0, JITTER_MS_MAX));
      }

      const item = scan.items[i];
      const symbolCallId = diagnosticsStore.recordCallStart(
        "OPTIONS.SNAPSHOT",
        "/v1/massive/options/snapshot/" + item.occSymbol
      );
      const symbolStartTime = Date.now();

      try {
        const entity = await buildOptionEntityEnrichedFromOccSymbol(item.occSymbol);
        writebacks.push({ address: item.address, entityCellValue: entity });
        refreshed++;
        diagnosticsStore.recordCallEnd(symbolCallId, {
          ok: true,
          ms: Date.now() - symbolStartTime,
        });
      } catch (error) {
        const errorMsg = safeErrorMessage(error);
        failed++;
        diagnosticsStore.recordCallEnd(symbolCallId, {
          ok: false,
          ms: Date.now() - symbolStartTime,
        });

        let errorCategory: DiagnosticErrorCategory = "unknown";
        if (error instanceof Error) {
          if (error.message.includes("Market data unavailable")) {
            errorCategory = "backend";
          } else if (error.message.includes("Invalid OCC symbol")) {
            errorCategory = "validation";
          } else {
            const raw = categorizeError(error);
            errorCategory = raw === "not_found" || raw === "bad_request" ? "unknown" : raw;
          }
        }

        diagnosticsStore.recordError({
          category: errorCategory,
          message: errorMsg,
          op_key: "OPTIONS.SNAPSHOT",
          endpoint: "/v1/massive/options/snapshot/" + item.occSymbol,
        });
        errors.push({ address: item.address, message: errorMsg });
      }

      done++;
      onProgress?.({
        done,
        total,
        refreshed,
        skipped,
        failed,
        currentSymbol: item.occSymbol,
      });
    }

    if (writebacks.length > 0) {
      try {
        await runExcel(async (context) => {
          const sheet = context.workbook.worksheets.getActiveWorksheet();
          for (const wb of writebacks) {
            const r = sheet.getRange(wb.address);
            r.valuesAsJson = [[wb.entityCellValue]];
          }
          await context.sync();
        });
        logger.log(
          "RefreshSheetOptions",
          "writeback completed",
          "cells=" + writebacks.length
        );
      } catch (writeError) {
        const writeMsg = safeErrorMessage(writeError);
        logger.error("RefreshSheetOptions", "write-back failed", writeError);
        diagnosticsStore.recordError({
          category: categorizeError(writeError),
          message: writeMsg,
          op_key: "OPTIONS.REFRESH_SHEET",
          endpoint: "/refresh-sheet-options",
        });
        errors.push({ address: "", message: "Write-back failed: " + writeMsg });
      }
    }

    diagnosticsStore.recordCallEnd(opCallId, {
      ok: errors.length === 0,
      ms: Date.now() - opStartTime,
    });

    logger.log(
      "RefreshSheetOptions",
      "completed",
      "refreshed=" + refreshed + " failed=" + failed + " skipped=" + skipped
    );

    return {
      errors,
      total,
      refreshed,
      failed,
      skipped,
    };
  } catch (error) {
    const errorMessage = safeErrorMessage(error);
    diagnosticsStore.recordCallEnd(opCallId, {
      ok: false,
      ms: Date.now() - opStartTime,
    });
    diagnosticsStore.recordError({
      category: categorizeError(error),
      message: errorMessage,
      op_key: "OPTIONS.REFRESH_SHEET",
      endpoint: "/refresh-sheet-options",
    });
    logger.error("RefreshSheetOptions", "action failed", error);
    throw error;
  }
}
